import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../../../axiosInstanceSetup";
import { mapFrontendStatusToApi, mapFrontendCategoryToApi } from "./featureConfig";

const COLORS = [
  "#FF9F40",
  "#FF6384",
  "#4BC0C0",
  "#36A2EB",
];

const BASE_URL = process.env.REACT_APP_API_URL;

const FeaturesChart = ({
  statusFilter = [],
  categoryFilter = [],
  columnSearch = {},
  activeSearchColumn = null,
  trigger = 0,
  startDate = null,
  endDate = null
}) => {
  const [categoryCount, setCategoryCount] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if(startDate){
          params.append("startDate",startDate);
        }
        if(endDate){
          params.append("endDate",endDate);
        }

        // Status filter
        if (Array.isArray(statusFilter) && statusFilter.length > 0) {
          const apiStatuses = statusFilter
            .map(status => mapFrontendStatusToApi(status))
            .filter(Boolean);

          if (apiStatuses.length > 0) {
            params.append("status", apiStatuses.join(","));
          }
        }

        // Category filter
        if (Array.isArray(categoryFilter) && categoryFilter.length > 0) {
          const apiCategories = categoryFilter
            .map(category => mapFrontendCategoryToApi(category))
            .filter(Boolean);

          if (apiCategories.length > 0) {
            params.append("category", apiCategories.join(","));
          }
        }

        // Column search (same logic as loadRequests)
        if (activeSearchColumn && columnSearch[activeSearchColumn]?.trim()) {
          params.append("search_by", activeSearchColumn);
          params.append("search_value", columnSearch[activeSearchColumn].trim());
        }

        // Build URL - only add query string if params exist
        const queryString = params.toString();
        const url = queryString
          ? `${BASE_URL}/features/metrics?${queryString}`
          : `${BASE_URL}/features/metrics`;

        const response = await axiosInstance.get(url);
        const data = response.data;

        if (data.featureCounts) {
          setCategoryCount(data.featureCounts);
        }
      } catch (error) {
        console.error("Error fetching feature metrics:", error);
        setCategoryCount({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [trigger]);

  const totalIncidents = useMemo(() => {
     if (categoryCount.Total !== undefined) {
      return categoryCount.Total;
    }
    return Object.values(categoryCount).reduce((a, b) => a + b, 0);
  }, [categoryCount]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-300 p-6 mb-[10px]">
        <div className="text-gray-600">Loading metrics...</div>
      </div>
    );
  }

  if (!Object.keys(categoryCount).length) {
    return (
      <div className="bg-white rounded-lg border border-gray-300 p-6 mb-[10px]">
        <div className="text-gray-600">No data available</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-300 p-6 mb-[10px]">
      <div className="flex gap-6">
        {Object.entries(categoryCount).map(([category, count], index) => {
          const percentage = ((count / totalIncidents) * 100).toFixed(0);
          return (
            <div key={category} className="flex items-center space-x-3">
              <div
                className="w-[5px] h-[30px] rounded-full"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <div>
                <div className="text-sm text-gray-600">
                  {category} ({percentage}%)
                </div>
                <div className="text-2xl font-bold text-gray-800">{count}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeaturesChart;